import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import {
  Box,
  Grid,
  Container,
  List,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { matchSorter } from "match-sorter";
import theme from "react-syntax-highlighter/dist/cjs/styles/hljs/vs2015";

import { useStore, observer } from "../../../service/mobx";
import { Section } from "..";
import { GetStarted } from ".";
import ButtonText from "../../../component/Button/Text";
import { ListItem } from "../../../component/List/ListItem";

const SyntaxHighlighter = dynamic(() => import("react-syntax-highlighter"), {
  ssr: false
});

function CodeBlocks({ modelList }) {
  const [view, setView] = useState(0);
  const { device } = useStore();
  const [code, description] = [
    [
      list,
      "List Hugging Face models available on Bytez, see how much RAM they use"
    ],
    [run, "Run a model in a few lines"],
    [quantized, "Run high performance models for less money"],
    [scaleUp, "Scale up concurrency for parallel processing"]
  ][view];

  return (
    <Section id="code" bgcolor="dark.surface.container.lowest" pt={10}>
      <Container disableGutters maxWidth="large">
        <Typography
          component="div"
          align="center"
          color="dark.surface.on.color"
          typography={{ compact: "headlineLg", expanded: "displayMd" }}
        >
          Made for developers
        </Typography>
        <Typography
          align="center"
          component="div"
          variant="headlineSm"
          color="dark.primary.color"
          typography={{ compact: "titleMd", expanded: "headlineSm" }}
        >
          An API that saves you time and money
        </Typography>
        <Container disableGutters maxWidth="expanded" sx={{ pt: 10 }}>
          <Stack flexWrap="wrap" direction="row">
            {["List models", "Run a model", "Quantize", "Scale up"].map(
              (title, index) => (
                <Tab
                  selected={index === view}
                  key={title}
                  title={title}
                  onClick={() => setView(index)}
                />
              )
            )}
          </Stack>
        </Container>
        <Box pt={5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            pb={2}
            flexWrap="wrap"
            alignItems="center"
          >
            <Typography
              typography={{ compact: "bodyLg", expanded: "headlineMd" }}
              color="dark.surface.on.color"
            >
              {description}
            </Typography>
            {device.isPhone ? null : <GetStarted />}
          </Stack>
          <Editor code={code} lines={[1, 2, 3, 4, 5]} />
        </Box>
        {view === 0 ? <ModelList modelList={modelList} /> : null}
      </Container>
    </Section>
  );
}

export default observer(CodeBlocks);
export const Tab = ({ title, onClick, Icon, selected, sx }) => (
  <ButtonText
    label={title}
    onClick={onClick}
    IconEnd={Icon}
    sx={{
      p: { compact: 1, expanded: 4 },
      bgcolor: selected ? "dark.secondary.container" : undefined,
      typography: {
        compact: "bodyMd",
        expanded: "headlineSm"
      },
      transition: theme => theme.transitions.create("all", { duration: 750 }),
      color: selected ? "dark.secondary.on.container" : "dark.surface.on.color",
      "&:hover": {
        color: "dark.secondary.color"
      },
      ...sx
    }}
  />
);
const Editor = ({ code }) => (
  <Box
    sx={theme => ({
      "& > pre": {
        m: 0,
        p: 0,
        borderRadius: theme.shape.md.round,
        bgcolor: theme.palette.dark.surface.color + " !important"
      },
      "& *": {
        fontSize: { compact: 12, expanded: 20 },
        typography: "code",
        borderBottomLeftRadius: "inherit",
        borderBottomRightRadius: "inherit"
      }
    })}
  >
    <SyntaxHighlighter showLineNumbers language="javascript" style={theme}>
      {code}
    </SyntaxHighlighter>
  </Box>
);
function ModelList({ modelList }) {
  const [list, setList] = useState([]);

  useEffect(() => {
    setList(modelList);
  }, [modelList]);

  return modelList?.length ? (
    <>
      <Grid
        container
        columns={10}
        spacing={3}
        mt={10}
        py={4}
        direction="row"
        justifyContent="space-around"
      >
        <Grid item compact={10} expanded>
          <Box
            p={2}
            pb={0}
            bgcolor="dark.surface.container.color"
            borderRadius={theme => theme.shape.md.round}
          >
            <Typography
              paragraph
              align="center"
              variant="headlineSm"
              color="dark.secondary.color"
            >
              Model List
            </Typography>
            <TextField
              fullWidth
              placeholder="HuggingFace model ID (org/model)"
              variant="outlined"
              onChange={event =>
                setList(
                  matchSorter(modelList, event.target.value, {
                    keys: ["name", "org", "ramRequired"].map(key => ({ key }))
                  })
                )
              }
              InputProps={{
                sx: {
                  borderRadius: theme => theme.shape.round,
                  color: "dark.secondary.color",
                  bgcolor: "dark.surface.container.high"
                }
              }}
            />
            <List sx={{ height: 400, overflowY: "scroll" }}>
              <Virtuoso
                data={list}
                itemContent={(_, { org, name, ramRequired }) => (
                  <ListItem
                    key={org + name}
                    dark
                    line1={
                      <>
                        <Box component="span" color="dark.secondary.color">
                          {`${org} / `}
                        </Box>
                        <Box component="span" color="dark.primary.color">
                          {name}
                        </Box>
                      </>
                    }
                    line2={`${Math.ceil(ramRequired)}gb RAM required`}
                  />
                )}
              />
            </List>
          </Box>
        </Grid>
        <Grid item compact expanded={5}>
          <Box pt={2} px={3}>
            <Typography
              paragraph
              align="center"
              variant="headlineSm"
              color="dark.primary.color"
            >
              {"Don't see the model you're looking for?"}
            </Typography>
            <Typography
              paragraph
              align="center"
              variant="bodyLg"
              sx={{ textWrap: "balance" }}
              color="dark.surface.on.color"
            >
              {"We've got you covered"}
            </Typography>
            <Typography
              paragraph
              align="center"
              variant="bodyLg"
              sx={{ textWrap: "balance" }}
              color="dark.surface.on.color"
            >
              Use the API below to make any model serverless
            </Typography>
            <Editor code={request} />
          </Box>
        </Grid>
      </Grid>
    </>
  ) : null;
}
const list = `
import Bytez from "bytez"
client = Bytez("api_key")

model_list = await client.list.models()

// scroll through the model_list below
`.trim();
const run = `
import Bytez from "bytez"
client = Bytez("api_key")

// load
model = await client.load("openai-community/gpt2")

// run
output = await model.run("Tell me a pirate story")

// optionally, stream
stream = await model.run("Who is Jolly Roger?", { stream: true })

// the serverless model automatically spins down if you stop using it
`.trim();
const scaleUp = `
import Bytez from "bytez"
client = Bytez("api_key")

// load a model and optionally set concurrency
model = await client.load("openai-community/gpt2", { concurrency: 3 })

// scale up, pay only for what you use
outputs = await Promise.all([
  model.run("Tell me a pirate story"),
  model.run("Jack and Jill went"),
  model.run("What are the days of the week?")
])
`.trim();
const request = `
import Bytez from "bytez"
client = Bytez("api_key")

await client.request.model(
  "huggingface_org/model_name"
)

`.trim();
const quantized = `
import Bytez from "bytez"
client = Bytez("api_key")

// load in 8-bit (2x-4x lower cost + faster)
model_8bit = await client.load("tiiuae/falcon-40b-instruct", { precision: 8 })
// load in 4-bit (4x-8x lower cost + faster)
model_4bit = await client.load("tiiuae/falcon-40b-instruct", { precision: 4 })
`.trim();
